import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import PropTypes from "prop-types";
import classNames from "classnames";

import Card from "shared/Card";
import InputItem from "Events/Components/InputItem";
import AddButton from "Events/Components/AddButton";
import { Select } from "shared/Select/Select";
import DatePicker from "Events/Components/DatePicker";
import TimePicker from "Events/Components/TimePicker";
import TextArea from "shared/TextArea";
import Button from "shared/Button";
import styles from "./SessionsV2Form.module.css";
import DeleteModal from "Events/Components/DeleteModal";
import AddSpeakerModal from "Events/Components/AddSpeakerModal";
import moment from "moment-timezone";
import Link from "shared/Link";
import { Modal } from "shared/Modal";
import Checkbox from "shared/@forms/Checkbox";
import Loader from "shared/Loader";

export function SessionsV2Form({
  sessions,
  setSessions,
  onAddSpeaker,
  eventData,
  pageMode,
  eventInfoTimeZone,
  isLocalV2 = false,
}) {
  const history = useHistory();
  const [showSpeakerModal, setShowSpeakerModal] = useState(false);
  const [indexToRemove, setIndexToRemove] = useState(0);
  const [sessionSpeakerIndex, setSessionSpeakerIndex] = useState(0);
  const [showTicketSetModal, setShowTicketSetModal] = useState(false);
  const [sessionTicketSetIndex, setSessionTicketSetIndex] = useState(0);
  const [selectedTicketSets, setSelectedTicketSets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [sessionsArray, setSessionsArray] = useState([]);
  const sessionsArrayRef = useRef([]);

  useEffect(() => {
    sessionsArrayRef.current = sessionsArray;
  }, [sessionsArray]);

  useEffect(() => {
    setSelectedTicketSets(
      (sessionsArray[sessionTicketSetIndex] &&
        (sessionsArray[sessionTicketSetIndex].visibleTo?.saved ||
          sessionsArray[sessionTicketSetIndex].visibleTo)) ||
        []
    );
  }, [sessionTicketSetIndex, sessionsArray]);

  const [showRemoveModal, changeShowRemoveModal] = useState(false);

  const timezoneLabelToIana = {
    "Pacific Time (PT)": "America/Los_Angeles",
    "Mountain Time (MT)": "America/Boise",
    "Central Time (CT)": "America/Chicago",
    "Eastern Time (ET)": "America/New_York",
    "Alaska Time (AKT)": "America/Anchorage",
    "Hawaii-Aleutian Time (HAT)": "America/Adak",
    "Hawaii-Honolulu (HST)": "Pacific/Honolulu",
    "Australian Eastern Standard Time (AEST)": "Australia/Sydney",
    "Australian Central Standard Time (ACST)": "Australia/Adelaide",
    "Australian Western Standard Time (AWST)": "Australia/Perth",
    "New Zealand Standard Time (NZST)": "Pacific/Auckland",
    "Australian Eastern Daylight Time (AEDT)": "Australia/Sydney",
    "New Zealand Daylight Time (NZDT)": "Pacific/Auckland",
  };

  const toIanaTimeZone = (timeZone) =>
    timezoneLabelToIana[timeZone] || timeZone || "America/Los_Angeles";

  const sessionTimeZoneRaw =
    eventData?.event?.dates?.eventStartTimeZone?.saved ||
    eventData?.event?.dates?.eventStartTimeZone?.value ||
    eventInfoTimeZone?.saved ||
    eventInfoTimeZone?.value ||
    eventInfoTimeZone;
  const sessionTimeZone = toIanaTimeZone(sessionTimeZoneRaw);

  const formatDateInSessionTimeZone = (isoString) =>
    isoString
      ? moment.utc(isoString).tz(sessionTimeZone).format("MM/DD/YYYY")
      : "";

  const formatTimeInSessionTimeZone = (isoString) =>
    isoString ? moment.utc(isoString).tz(sessionTimeZone).format("HH:mm") : "";

  const formatDateTimeInSessionTimeZone = (isoString) =>
    isoString
      ? moment.utc(isoString).tz(sessionTimeZone).format("MM/DD/YYYY hh:mm A")
      : "N/A";

  useEffect(() => {
    const processSessionData = (session) => {
      return Object.fromEntries(
        Object.entries(session).map(([key, details]) => {
          if (
            details &&
            typeof details === "object" &&
            "saved" in details &&
            "value" in details
          ) {
            return [key, details.saved ?? details.value ?? ""];
          }
          return [key, details];
        })
      );
    };

    const sessionData = sessions.map(processSessionData);
    setSessionsArray(sessionData);
  }, [sessions]);

  const handleSessionAdd = () => {
    setIsLoading(true);

    setSessions(
      [
        ...sessionsArray,
        {
          capacity: null,
          checkInStartDate: null,
          description: null,
          doorsOpenStartDate: null,
          endDate: null,
          id: null,
          location: null,
          name: null,
          scannable: null,
          speakers: [],
          startDate: null,
          visibility: null,
          visibleTo: [],
          _id: "",
        },
      ],
      true
    ).then(() => setIsLoading(false));
  };

  const debounceTimer = useRef(null);
  const pendingUpdates = useRef({});

  const isSavedObj = (v) =>
    v &&
    typeof v === "object" &&
    !Array.isArray(v) &&
    ("saved" in v || "value" in v);

  const flattenField = (v) => (isSavedObj(v) ? v.saved ?? v.value ?? null : v);

  const isDateField = (key) =>
    key === "startDate" ||
    key === "endDate" ||
    key === "checkInStartDate" ||
    key === "checkInEndDate" ||
    key === "doorsOpenStartDate" ||
    key === "doorsOpenEndDate";

  const isTimeField = (key) =>
    key === "startTime" ||
    key === "endTime" ||
    key === "checkInStartTime" ||
    key === "checkInEndTime" ||
    key === "doorsOpenStartTime" ||
    key === "doorsOpenEndTime";

  const buildIsoFromDateInput = (baseIso, newDateString) => {
    if (!newDateString) return null;

    const newDateMoment = moment.tz(
      newDateString,
      "MM/DD/YYYY",
      sessionTimeZone
    );
    const baseMoment = baseIso
      ? moment.utc(baseIso).tz(sessionTimeZone)
      : moment.tz(sessionTimeZone).startOf("day");

    return baseMoment
      .year(newDateMoment.year())
      .month(newDateMoment.month())
      .date(newDateMoment.date())
      .second(0)
      .millisecond(0)
      .utc()
      .toISOString();
  };

  const buildIsoFromTimeInput = (baseIso, newTimeString) => {
    if (!newTimeString) return null;

    const newTimeMoment = moment.tz(newTimeString, "HH:mm", sessionTimeZone);
    const baseMoment = baseIso
      ? moment.utc(baseIso).tz(sessionTimeZone)
      : moment.tz(sessionTimeZone).startOf("day");

    return baseMoment
      .hour(newTimeMoment.hour())
      .minute(newTimeMoment.minute())
      .second(0)
      .millisecond(0)
      .utc()
      .toISOString();
  };

  const updateSession = (index, key, value, doRefetch) => {
    const cleanVal = flattenField(value);

    let normalizedUiValue = cleanVal;

    setSessionsArray((prev) => {
      const copy = [...prev];
      const currentSession = copy[index] || {};

      if (isDateField(key)) {
        normalizedUiValue = cleanVal
          ? buildIsoFromDateInput(currentSession[key], cleanVal)
          : null;
      } else if (isTimeField(key)) {
        const dateKey = key.replace("Time", "Date");
        normalizedUiValue = cleanVal
          ? buildIsoFromTimeInput(currentSession[dateKey], cleanVal)
          : null;
      }

      copy[index] = { ...currentSession, [key]: normalizedUiValue };
      return copy;
    });

    if (!pendingUpdates.current[index]) pendingUpdates.current[index] = {};

    if (isDateField(key) || isTimeField(key)) {
      const storageKey = isTimeField(key) ? key.replace("Time", "Date") : key;
      pendingUpdates.current[index][storageKey] = normalizedUiValue;
    } else {
      pendingUpdates.current[index][key] = cleanVal;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      const updatesForIndex = pendingUpdates.current[index];
      if (!updatesForIndex) return;

      const currentSessions = sessionsArrayRef.current;
      const sessionToUpdate = currentSessions[index];

      if (!sessionToUpdate) {
        delete pendingUpdates.current[index];
        return;
      }

      const updatedFields = {};

      for (const [fieldKey, fieldValue] of Object.entries(updatesForIndex)) {
        const cleanFieldValue = flattenField(fieldValue);

        if (fieldKey === "visibility" && cleanFieldValue === "All") {
          updatedFields.visibleTo = [];
        }

        updatedFields[fieldKey] = cleanFieldValue;
      }

      const nextSessions = [...sessions];
      const originalStructuredSession = nextSessions[index];

      if (!originalStructuredSession) {
        delete pendingUpdates.current[index];
        return;
      }

      const updatedStructuredSession = { ...originalStructuredSession };

      for (const [fieldKey, fieldValue] of Object.entries(updatedFields)) {
        const existingField = updatedStructuredSession[fieldKey];

        if (
          existingField &&
          typeof existingField === "object" &&
          !Array.isArray(existingField) &&
          ("saved" in existingField || "value" in existingField)
        ) {
          updatedStructuredSession[fieldKey] = {
            ...existingField,
            saved: fieldValue,
          };
        } else {
          updatedStructuredSession[fieldKey] = fieldValue;
        }
      }

      nextSessions[index] = updatedStructuredSession;
      setSessions(nextSessions, doRefetch);

      delete pendingUpdates.current[index];
    }, 800);
  };

  const onRemove = () => {
    const formattedSessions = sessionsArray.filter(
      (session, index) => index !== indexToRemove
    );

    setSessions(formattedSessions, true);
    changeShowRemoveModal(false);
  };

  const handleAddSpeaker = (speakers) => {
    const currentSpeakers = sessionsArray[sessionSpeakerIndex]?.speakers || [];

    const newSpeakers = speakers.filter(
      (speaker) =>
        !currentSpeakers.find(
          (existingSpeaker) => existingSpeaker.wwgId === speaker.wwgId
        )
    );

    const updatedSpeakers = [...currentSpeakers, ...newSpeakers];

    updateSession(sessionSpeakerIndex, "speakers", updatedSpeakers, true);
  };

  const handleRemoveSpeaker = (speaker, sessionIndex) => {
    setSessionsArray((prevSessions) => {
      const updatedSessions = prevSessions.map((session, idx) => {
        if (idx === sessionIndex) {
          return {
            ...session,
            speakers: session.speakers.filter(
              (speak) => speak.wwgId !== speaker.wwgId
            ),
          };
        }
        return session;
      });

      return [...updatedSessions];
    });

    const updatedSessions = sessionsArray.map((session, idx) => {
      if (idx === sessionIndex) {
        return {
          ...session,
          speakers: session.speakers.filter(
            (speak) => speak.wwgId !== speaker.wwgId
          ),
        };
      }
      return session;
    });

    setSessions(updatedSessions, true);
  };

  const renderAddSpeaker = (index) => {
    const speakerPermission = eventData?.speaker?.permission;

    const speakerSaved = sessionsArray[index]?.speakers;
    if (speakerPermission === "write" || speakerPermission === undefined) {
      if (speakerSaved) {
        return (
          <>
            <div className={speakerSaved.length > 0 ? styles.speakerList : ""}>
              {speakerSaved.map((speaker) => (
                <div
                  className={styles.speakerItem}
                  key={speaker.name || speaker.iboNumber}
                >
                  <Link to={"/events/events-details"}>
                    {speaker?.name ||
                      speaker?.iboNumber ||
                      "No Name For Speaker"}
                  </Link>
                  <div
                    className={styles.removeSpeaker}
                    onClick={() => handleRemoveSpeaker(speaker, index)}
                  >
                    &times;
                  </div>
                </div>
              ))}
            </div>
            <div
              className={classNames(styles["button-container"])}
              onClick={() => {
                setSessionSpeakerIndex(index);
                setShowSpeakerModal(true);
              }}
            >
              <AddButton description={"Add Speaker"} color="#2A3847" />
            </div>
          </>
        );
      }
      return (
        <div
          className={classNames(styles["button-container"])}
          onClick={() => {
            setSessionSpeakerIndex(index);
            setShowSpeakerModal(true);
          }}
        >
          <AddButton description={"Add Speaker"} color="#2A3847" />
        </div>
      );
    }
  };

  const renderViewSpeakerNames = (speakers) => {
    if (!speakers || speakers.length === 0) {
      return "N/A";
    }
    return speakers.map((speaker) => speaker.name).join(", ");
  };

  const handleAddTicketSets = () => {
    const ticketSetsOnly = selectedTicketSets.map((item) => {
      return {
        ticketSet: item.ticketSet || item.ticketSet,
        mongoId: item?._id,
      };
    });
    updateSession(sessionTicketSetIndex, "visibleTo", ticketSetsOnly);
  };

  const handleRemoveTicketSet = (ticketSet, sessionIndex) => {
    const ticketSetsArray =
      sessionsArray[sessionIndex]?.visibleTo?.saved ||
      sessionsArray[sessionIndex]?.visibleTo ||
      [];
    const ticketSetsToSend = ticketSetsArray.filter(
      (set) => set.id !== ticketSet.id
    );
    updateSession(sessionIndex, "visibleTo", ticketSetsToSend, true);
  };

  const renderAddTicketSets = (index) => {
    const ticketSetsToUse =
      sessionsArray[index]?.visibleTo?.saved || sessionsArray[index]?.visibleTo;

    if (ticketSetsToUse) {
      return (
        <>
          <div className={ticketSetsToUse.length > 0 ? styles.speakerList : ""}>
            {ticketSetsToUse.map((ticketSet) => (
              <div className={styles.speakerItem} key={ticketSet.ticketSet}>
                {ticketSet?.ticketSet
                  ? `${ticketSet.ticketSet} Ticket Set`
                  : "No Name For Ticket Set"}
                <div
                  className={styles.removeSpeaker}
                  onClick={() => handleRemoveTicketSet(ticketSet, index)}
                >
                  &times;
                </div>
              </div>
            ))}
          </div>
          <div
            className={classNames(styles["button-container"])}
            onClick={() => {
              setSessionTicketSetIndex(index);
              setShowTicketSetModal(true);
            }}
          >
            <AddButton description={"Add Ticket Set"} color="#2A3847" />
          </div>
        </>
      );
    }
    return (
      <div
        className={classNames(styles["button-container"])}
        onClick={() => {
          setSessionTicketSetIndex(index);
          setShowTicketSetModal(true);
        }}
      >
        <AddButton description={"Add Ticket Set"} color="#2A3847" />
      </div>
    );
  };

  const renderViewTicketSets = (session) => {
    const ticketSets = session.visibleTo || [];

    if (!Array.isArray(ticketSets) || ticketSets.length === 0) return "N/A";

    return ticketSets
      .map((set) => set.ticketSet || "Unnamed Ticket Set")
      .join(", ");
  };

  const redirectInvitees = () => {
    history.push("/events/manage-invitees");
  };

  if (pageMode === "view") {
    return (
      <Card
        children={
          <section>
            <div className={styles.header}>
              <div className={styles.title}>Sessions</div>
            </div>
            <div className={styles.container}>
              {sessionsArray.map((session, index) => (
                <div key={index} className={styles.section}>
                  <hr className={styles.hr} />
                  <div className={styles.row}>
                    <span className={styles.label}>Visibility</span>
                    <span className={styles.textValue}>
                      {session.visibility || "N/A"}
                    </span>
                  </div>
                  {session.visibility === "Limit To Groups" && (
                    <div className={styles.row}>
                      <div className={styles.leftCol}>
                        <span className={styles.label}>Ticket Sets</span>
                        <span className={styles.textValue}>
                          {renderViewTicketSets(session)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className={styles.row}>
                    <div className={styles.leftCol}>
                      <span className={styles.label}>Scannable</span>
                      <span className={styles.checkbox}>
                        <Checkbox
                          checked={session.scannable || false}
                          disabled
                        />
                      </span>
                    </div>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>Session Name</span>
                    <span className={styles.textValue}>
                      {session.name || "N/A"}
                    </span>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>Description</span>
                    <span className={styles.textValue}>
                      {session.description || "N/A"}
                    </span>
                  </div>
                  {!isLocalV2 && (
                    <>
                      <div className={styles.row}>
                        <span className={styles.label}>Speakers</span>
                        <span className={styles.textValue}>
                          {renderViewSpeakerNames(session.speakers)}
                        </span>
                      </div>
                      <div className={styles.row}>
                        <span className={styles.label}>Location</span>
                        <span className={styles.textValue}>
                          {session.location || "N/A"}
                        </span>
                      </div>

                      <div className={styles.row}>
                        <span className={styles.label}>Capacity</span>
                        <span className={styles.textValue}>
                          {session.capacity || "N/A"}
                        </span>
                      </div>
                    </>
                  )}
                  <div className={styles.row}>
                    <span className={styles.label}>Session Dates</span>
                    <div>
                      <span className={styles.viewDatesLabel}>Starts On: </span>
                      <span className={styles.viewDates}>
                        {session.startDate
                          ? formatDateTimeInSessionTimeZone(session.startDate)
                          : "N/A"}
                      </span>
                      <span className={styles.viewTimezones}>
                        {eventInfoTimeZone?.value}
                      </span>
                      <br />
                      <span className={styles.viewDatesLabel}>Ends On: </span>
                      <span className={styles.viewDates}>
                        {session.endDate
                          ? formatDateTimeInSessionTimeZone(session.endDate)
                          : "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className={styles.row}>
                    <span className={styles.label}>Check-In</span>
                    <div>
                      <span className={styles.viewDatesLabel}>Starts On: </span>
                      <span className={styles.viewDates}>
                        {session.checkInStartDate
                          ? formatDateTimeInSessionTimeZone(
                              session.checkInStartDate
                            )
                          : "N/A"}
                      </span>
                      <span className={styles.viewTimezones}>
                        {eventInfoTimeZone?.value}
                      </span>
                      <br />
                    </div>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>Doors Open</span>
                    <div>
                      <span className={styles.viewDatesLabel}>Starts On:</span>
                      <span className={styles.viewDates}>
                        {session.doorsOpenStartDate
                          ? formatDateTimeInSessionTimeZone(
                              session.doorsOpenStartDate
                            )
                          : "N/A"}
                      </span>
                      <span className={styles.viewTimezones}>
                        {eventInfoTimeZone?.value}
                      </span>
                      <br />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        }
      />
    );
  }

  return (
    <Card
      children={
        <section>
          <div className={styles.header}>
            <div className={styles.title}>Sessions</div>
            <div className={styles.button}>
              {isLoading ? (
                <Loader size={45} />
              ) : (
                <Button
                  children="Add"
                  size="small"
                  onClick={handleSessionAdd}
                />
              )}
            </div>
          </div>
          <div className={styles.container}>
            {sessionsArray.map((session, index) => (
              <div key={index} className={styles.section}>
                <hr className={styles.hr} />
                <div className={styles.row}>
                  <span className={styles.label}>
                    Visibility{" "}
                    {sessions[index]?.visibility?.required &&
                    pageMode === "edit"
                      ? " *"
                      : ""}
                  </span>
                  <span className={styles.dropdown}>
                    <Select
                      placeholder={
                        session.visibility?.saved ||
                        session.visibility ||
                        "Select"
                      }
                      value={session.visibility?.saved || session.visibility}
                      selectedOption={
                        session.visibility?.saved ||
                        session.visibility ||
                        "Select"
                      }
                      options={["All", "Limit To Groups"]}
                      onChange={(type) =>
                        updateSession(index, "visibility", type)
                      }
                      removeSelectedOption={false}
                    />
                  </span>
                  <div className={styles.selectRemove}>
                    {session.type === "Invite Only" && (
                      <div className={styles.button}>
                        <Button
                          children="Manage Invitees"
                          size="small"
                          onClick={redirectInvitees}
                        />
                      </div>
                    )}

                    <div className={styles.remove}>
                      <Button
                        className={styles.removeButton}
                        children="Remove"
                        size="small"
                        onClick={() => {
                          setIndexToRemove(index);
                          changeShowRemoveModal(true);
                        }}
                      />
                    </div>
                  </div>
                </div>

                {session.visibility == "Limit To Groups" && (
                  <div className={styles.row}>
                    <div className={styles.leftCol}>
                      <span className={styles.label}>Ticket Sets *</span>
                      {renderAddTicketSets(index)}
                    </div>
                  </div>
                )}

                <div className={styles.row}>
                  <div className={styles.leftCol}>
                    <span className={styles.label}>Scannable</span>
                    <span className={styles.checkbox}>
                      <Checkbox
                        checked={session.scannable || false}
                        onChange={(e) =>
                          updateSession(index, "scannable", e.target.checked)
                        }
                      />
                    </span>
                  </div>
                </div>

                {isLocalV2 ? (
                  <div className={styles.row}>
                    <span className={styles.label}>
                      Session Name
                      {sessions[index]?.name?.required && pageMode === "edit"
                        ? " *"
                        : ""}
                    </span>
                    <span className={styles.dropdown}>
                      <Select
                        placeholder={session.name || "Select"}
                        value={session.name || ""}
                        selectedOption={session.name || "Select"}
                        options={["Second Look Meeting\u2122", "Seminar"]}
                        onChange={(value) =>
                          updateSession(index, "name", value)
                        }
                        removeSelectedOption={false}
                      />
                    </span>
                  </div>
                ) : (
                  <div className={styles.session}>
                    <InputItem
                      label={`Session Name${
                        sessions[index]?.name?.required && pageMode === "edit"
                          ? " *"
                          : ""
                      }`}
                      placeholder="Click to type"
                      value={session.name || ""}
                      onChange={(e) =>
                        updateSession(index, "name", e.target.value)
                      }
                    />
                  </div>
                )}

                <div className={classNames(styles.description)}>
                  <TextArea
                    label={"Description"}
                    placeholder="Click to type"
                    value={session.description || ""}
                    onChange={(e) =>
                      updateSession(index, "description", e.target.value)
                    }
                    maxLength={500}
                  />
                </div>

                {!isLocalV2 && (
                  <>
                    <div className={styles.row}>
                      <div className={styles.leftCol}>
                        <span className={styles.label}>Speakers</span>
                        {renderAddSpeaker(index)}
                      </div>
                    </div>

                    <div className={styles.session}>
                      <InputItem
                        label="Location"
                        placeholder="Click to type"
                        value={session.location || ""}
                        onChange={(e) =>
                          updateSession(index, "location", e.target.value)
                        }
                      />
                    </div>

                    <div className={classNames(styles.session)}>
                      <InputItem
                        label="Capacity"
                        placeholder="Click to type"
                        value={session.capacity || ""}
                        onChange={(e) =>
                          updateSession(index, "capacity", e.target.value)
                        }
                      />
                    </div>
                  </>
                )}

                <div className={styles.datesItemFirst}>
                  <span className={styles.datesLabel}>
                    <span className={styles.datesMainLabel}>
                      Session Dates{" "}
                      {sessions[index]?.startDate?.required &&
                      pageMode === "edit"
                        ? " *"
                        : ""}
                    </span>
                  </span>

                  <div className={styles.datesSubItemContainer}>
                    <div className={styles.datesSubItem}>
                      <span className={styles.datesSubLabel}>Starts On:</span>
                      <span className={styles.datePicker}>
                        <DatePicker
                          dateString={formatDateInSessionTimeZone(
                            session.startDate
                          )}
                          setDate={(sDate) =>
                            updateSession(index, "startDate", sDate || null)
                          }
                          isSession={true}
                        />
                      </span>
                      <span className={styles.timePicker}>
                        <TimePicker
                          timeString={formatTimeInSessionTimeZone(
                            session.startDate
                          )}
                          onChange={(sTime) =>
                            updateSession(index, "startTime", sTime)
                          }
                        />
                      </span>
                      <span className={styles.timezone}>
                        {eventData.event?.dates?.eventStartTimeZone?.saved ||
                          eventData.event?.dates?.eventStartTimeZone?.value ||
                          ""}
                      </span>
                    </div>
                    <div className={styles.datesSubItem}>
                      <span className={styles.datesSubLabel}>Ends On:</span>
                      <span className={styles.datePicker}>
                        <DatePicker
                          dateString={formatDateInSessionTimeZone(
                            session.endDate
                          )}
                          setDate={(sDate) =>
                            updateSession(index, "endDate", sDate || null)
                          }
                          isSession={true}
                        />
                      </span>
                      <span className={styles.timePicker}>
                        <TimePicker
                          timeString={formatTimeInSessionTimeZone(
                            session.endDate
                          )}
                          onChange={(eTime) =>
                            updateSession(index, "endTime", eTime || null)
                          }
                        />
                      </span>
                      <span className={styles.timezone}>
                        {eventData.event?.dates?.eventStartTimeZone?.saved ||
                          eventData.event?.dates?.eventStartTimeZone?.value ||
                          ""}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <DeleteModal
            header={"REMOVE SESSION"}
            description={"Confirm that you would like to remove session?"}
            isOpen={showRemoveModal}
            onCollapse={() => changeShowRemoveModal(false)}
            onClick={() => onRemove()}
            btnName={"REMOVE"}
          />
          <AddSpeakerModal
            isOpen={showSpeakerModal}
            onCollapse={() => {
              setSessionSpeakerIndex(0);
              setShowSpeakerModal(false);
            }}
            onClick={(speakers) => handleAddSpeaker(speakers)}
          />
          <Modal
            title="Ticket Sets"
            isOpen={showTicketSetModal}
            onClose={() => setShowTicketSetModal(false)}
            children={
              <div className={styles.modal}>
                <div className={styles.optionsListContainer}>
                  <div className={styles.optionsListBody}>
                    {(eventData.tickets || []).map((ticketSet) => {
                      return (
                        <div
                          className={styles.optionItemContainer}
                          key={
                            ticketSet.ticketSet.saved ||
                            ticketSet.ticketSet.value
                          }
                        >
                          <div className={styles.optionItem}>
                            <Checkbox
                              className={styles.optionCheckbox}
                              onChange={() => {
                                // Determine the correct ticket set value based on logic
                                const ticketSetValue =
                                  typeof ticketSet.ticketSet !== "object"
                                    ? ticketSet.ticketSet
                                    : ticketSet.ticketSet.saved ??
                                      ticketSet.ticketSet.value;

                                const isAdded = selectedTicketSets.some(
                                  (tktSet) =>
                                    JSON.stringify(tktSet.ticketSet) ===
                                    JSON.stringify(ticketSetValue)
                                );

                                if (isAdded) {
                                  setSelectedTicketSets(
                                    selectedTicketSets.filter(
                                      (tktSet) =>
                                        JSON.stringify(tktSet.ticketSet) !==
                                        JSON.stringify(ticketSetValue)
                                    )
                                  );
                                } else {
                                  setSelectedTicketSets([
                                    ...selectedTicketSets,
                                    {
                                      id: ticketSet.id,
                                      mongoId: ticketSet._id || ticketSet.id,
                                      ticketSet: ticketSetValue,
                                      _id: ticketSet._id,
                                    },
                                  ]);
                                }
                              }}
                              checked={selectedTicketSets.some(
                                (tktSet) =>
                                  JSON.stringify(tktSet.ticketSet) ===
                                  JSON.stringify(
                                    typeof ticketSet.ticketSet !== "object"
                                      ? ticketSet.ticketSet
                                      : ticketSet.ticketSet.saved ??
                                          ticketSet.ticketSet.value
                                  )
                              )}
                            />
                          </div>
                          <div className={styles.optionItem}>
                            {!ticketSet.ticketSet.saved &&
                            !ticketSet.ticketSet.value
                              ? `${ticketSet.ticketSet} Ticket Set`
                              : ticketSet.ticketSet?.saved
                              ? `${ticketSet?.ticketSet.saved} Ticket Set`
                              : `${ticketSet?.ticketSet.value} Ticket Set` ||
                                "No Name For Ticket Set"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <Button
                  className={styles.updateButton}
                  children={"Update"}
                  onClick={() => {
                    handleAddTicketSets();
                    setSelectedTicketSets([]);
                    setShowTicketSetModal(false);
                  }}
                />
              </div>
            }
          />
        </section>
      }
    />
  );
}

SessionsV2Form.propTypes = {
  type: PropTypes.string,
  ticketGroups: PropTypes.arrayOf(PropTypes.string),
  sessionName: PropTypes.string,
  setSessionName: PropTypes.func,
  description: PropTypes.string,
  setDescription: PropTypes.func,
  speakers: PropTypes.arrayOf(PropTypes.shape({})),
  setSpeakers: PropTypes.func,
  rooms: PropTypes.arrayOf(PropTypes.string),
  room: PropTypes.string,
  setRoom: PropTypes.func,
  startDate: PropTypes.string,
  setStartDate: PropTypes.func,
  endDate: PropTypes.string,
  setEndDate: PropTypes.func,
  startTime: PropTypes.string,
  setStartTime: PropTypes.func,
  endTime: PropTypes.string,
  setEndTime: PropTypes.func,
};
